import { Box, Typography, IconButton, Button, TextField } from '@mui/material';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import { FeedbackType, PostCallFeedbackState } from './types';
import { INITIAL_POST_CALL_FEEDBACK_STATE } from './initialPostCallFeedbackState.const';
import FeedbackActions from './FeedbackActions';
import { StringHelper } from '@act/shared/utilities';
import { useTracking } from '../../../tracking';

const ISSUES_MAP: Record<FeedbackType, string[]> = {
  summary: ['missing-words', 'missing-topics'] as const,
  transcription: [
    'missing-words',
    'missing-topics',
    'only-one-speaker',
  ] as const,
} as const;

interface NegativeQuestionProps {
  feedbackType: FeedbackType;
  postCallFeedback: PostCallFeedbackState;
  isUpdatingPostCallFeedback: boolean;
  setPostCallFeedback: (postCallFeedback: PostCallFeedbackState) => void;
  onClose: () => void;
  submitFeedback: (feedback?: PostCallFeedbackState) => Promise<void>;
}

const NegativeQuestion = ({
  feedbackType,
  postCallFeedback,
  isUpdatingPostCallFeedback,
  setPostCallFeedback,
  onClose,
  submitFeedback,
}: NegativeQuestionProps) => {
  const isSummaryFeedback = feedbackType === 'summary';
  const feedbackValue = isSummaryFeedback
    ? postCallFeedback.summaryAccuracyFeedback
    : postCallFeedback.accuracyFeedback;

  const tracking = useTracking();
  const { generateTrackingProps } = tracking || {};

  function goBack() {
    setPostCallFeedback(INITIAL_POST_CALL_FEEDBACK_STATE);
  }

  function getVariant(issue: string) {
    if (
      postCallFeedback[
        feedbackType === 'summary' ? 'summaryAccuracyIssues' : 'accuracyIssues'
      ]?.includes(issue)
    ) {
      return 'contained';
    }
    return 'outlined';
  }

  function toggleIssue(issue: string) {
    const updatedFeedback = { ...postCallFeedback };

    if (isSummaryFeedback) {
      if (updatedFeedback.summaryAccuracyIssues?.includes(issue)) {
        updatedFeedback.summaryAccuracyIssues =
          updatedFeedback.summaryAccuracyIssues?.filter(i => i !== issue);
      } else {
        updatedFeedback.summaryAccuracyIssues = [
          ...updatedFeedback.summaryAccuracyIssues,
          issue,
        ];
      }
    } else {
      if (updatedFeedback.accuracyIssues?.includes(issue)) {
        updatedFeedback.accuracyIssues = updatedFeedback.accuracyIssues?.filter(
          i => i !== issue
        );
      } else {
        updatedFeedback.accuracyIssues = [
          ...updatedFeedback.accuracyIssues,
          issue,
        ];
      }
    }

    setPostCallFeedback(updatedFeedback);
  }

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedFeedback = { ...postCallFeedback };
    const newFeedbackValue = e.target.value;

    if (isSummaryFeedback) {
      updatedFeedback.summaryAccuracyFeedback = newFeedbackValue;
    } else {
      updatedFeedback.accuracyFeedback = newFeedbackValue;
    }

    setPostCallFeedback(updatedFeedback);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
          marginBottom: '8px',
        }}
      >
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {ISSUES_MAP[feedbackType].map(issue => (
            <Button
              key={issue}
              size="small"
              variant={getVariant(issue)}
              {...(generateTrackingProps
                ? generateTrackingProps(`${issue}-issue-button`)
                : {})}
              onClick={() => toggleIssue(issue)}
            >
              {StringHelper.sentenceCase(issue, true)}
            </Button>
          ))}
        </Box>
        <Box>
          <TextField
            label="Feedback (optional)"
            sx={{ width: '100%' }}
            multiline
            rows={2}
            size="small"
            value={feedbackValue}
            onChange={handleFeedbackChange}
            {...(generateTrackingProps
              ? generateTrackingProps('feedback-text-input')
              : {})}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <IconButton
          {...(generateTrackingProps
            ? generateTrackingProps('go-back-button')
            : {})}
          disabled={isUpdatingPostCallFeedback}
          onClick={goBack}
        >
          <KeyboardBackspaceOutlinedIcon color="info" />
        </IconButton>

        <Typography variant="body2">What went wrong?</Typography>

        <IconButton disabled={true}>
          <ThumbDownAltOutlinedIcon color="error" />
        </IconButton>

        <Box sx={{ marginLeft: 'auto' }}>
          <FeedbackActions
            feedbackType={feedbackType}
            onClose={onClose}
            submitFeedback={submitFeedback}
            isUpdatingPostCallFeedback={isUpdatingPostCallFeedback}
            postCallFeedback={postCallFeedback}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default NegativeQuestion;
